import React, {
  FC,
  ReactElement,
  useState,
  useEffect,
  useMemo,
  useContext,
} from 'react'
import styled, { ThemeContext } from 'styled-components'
import Menu from '../Menu'
import Card from '../Card'
import { device } from '../breakpoints'
import useFetch from 'react-fetch-hook'
// @ts-ignore
import { csvParse } from 'd3-dsv'
import { useSpring, animated } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'
import ToggleButton from '../ToggleButton'

// const categoriesOrder = [
//   'Troubles neurologiques',
//   'Poitrine',
//   'Poumons',
//   'Ventre',
//   'Membres',
//   'Peau',
// ]

const MemoizedMenu = React.memo(Menu)

const ExampleComponent = ({ lang }: { lang: 'de' | 'fr' | undefined }) => {
  const [canvasIsLoading, setCanvasIsLoading] = useState(true)
  const [stepRefs, setStepRefs] = useState<Map<number, HTMLDivElement>>(
    new Map()
  )
  const [started, setStarted] = useState(false)
  const [symptomId, setSymptomId] = useState<number>(0)
  const [symptoms, setSymptoms] = useState<Symptom[]>([])
  const [selectedSymptoms, setSelectedSymptoms] = useState<Symptom[]>([])
  const [age, setAge] = useState<'adults' | 'children'>('adults')
  const isMobile = window.innerWidth < 768
  const themeContext = useContext(ThemeContext)
  const [{ x }, api] = useSpring(() => ({ x: 0 }))
  const bind = useDrag(
    ({ down, movement: [mx, my] }) => {
      api.start({ x: down ? mx : 0, immediate: down })
    },
    { axis: 'x' }
  )

  const { isLoading, data, error } = useFetch(
    require(`../../symptoms-${lang}.csv`),
    {
      formatter: (response) => response.text(),
    }
  )

  const handleMenuClick = useMemo(
    () => (id: number) => {
      setSymptomId(id)
    },
    [stepRefs]
  )

  useEffect(() => {
    if (data) {
      setSymptoms(
        csvParse(data, (d: any) => ({
          id: +d.id,
          name: d.symptome,
          fileName: d.name,
          position: d.position,
          category: d.categorie,
          icon: d.icone,
          posx: +d.x,
          posy: +d.y,
          pctAdult: +d.adultes,
          pctChild: +d.enfants,
          textChild: d['texte enfants'],
          textAdult: d['texte adultes'],
          linkAdult: d['lien adultes'],
          linkChild: d['lien enfants'],
        }))
      )
    }
  }, [data])

  // const selectedSymptoms = useMemo(
  //   () =>
  //     symptoms
  //       .map((s) => ({
  //         ...s,
  //         pct: age === 'adults' ? s.pctAdult : s.pctChild,
  //         text: age === 'adults' ? s.textAdult : s.textChild,
  //         link: age === 'adults' ? s.linkAdult : s.linkChild,
  //       }))
  //       .sort(
  //         (a, b) =>
  //           // categoriesOrder.findIndex((v) => v === a.category) -
  //           //   categoriesOrder.findIndex((v) => v === b.category) ||
  //           b.pct - a.pct
  //       )
  //       .map((s, i) => ({ ...s, order: i })),
  //   [symptoms, age]
  // )

  useEffect(() => {
    const newSymptomsList = symptoms
      .map((s) => ({
        ...s,
        pct: age === 'adults' ? s.pctAdult : s.pctChild,
        text: age === 'adults' ? s.textAdult : s.textChild,
        link: age === 'adults' ? s.linkAdult : s.linkChild,
      }))
      .sort(
        (a, b) =>
          // categoriesOrder.findIndex((v) => v === a.category) -
          //   categoriesOrder.findIndex((v) => v === b.category) ||
          b.pct - a.pct
      )
      .map((s, i) => ({ ...s, order: i }))

    if (selectedSymptoms.length > 0) {
      // console.log(symptomId)
      // console.log(selectedSymptoms[symptomId].id)
      const newId = newSymptomsList.findIndex(
        (s) => s.id === selectedSymptoms[symptomId].id
      )

      setSymptomId(newId)
    }

    setSelectedSymptoms(newSymptomsList)
  }, [symptoms, age])

  if (error) {
    return (
      <div>
        <p>Code: ${error.status}</p>
        <p>Message: ${error.statusText}</p>
      </div>
    )
  }

  return (
    <>
      {/* <Stage {...bind()} style={{x}}> */}
      <Stage>
        <div style={{ zIndex: 0, position: 'relative' }}></div>

        <ArrowPrev
          onClick={() => {
            setSymptomId(
              symptomId !== null
                ? symptomId - 1 < 0
                  ? selectedSymptoms.length - 1
                  : symptomId - 1
                : 0
            )
          }}
        >
          <ArrowElementLeft />
        </ArrowPrev>

        <ArrowNext
          onClick={() => {
            setSymptomId(
              symptomId !== null ? (symptomId + 1) % selectedSymptoms.length : 0
            )
          }}
        >
          <ArrowElementRight />
        </ArrowNext>

        {/* <CanvasVideoContainer>

          <CanvasVideoPlayer
            adults={age === 'adults'}
            started={started}
            onLoaded={() => {
              setCanvasIsLoading(false)
            }}
            onMounted={() => {}}
            range={
              [symptomId * 9, symptomId * 9 + 9]
            }
            speed={200}
          />
        </CanvasVideoContainer> */}

        <VideoContainer
          onClick={() => {
            setSymptomId(
              symptomId !== null ? (symptomId + 1) % selectedSymptoms.length : 0
            )
          }}
        >
          <VideoContent>
            {selectedSymptoms[symptomId] && (
              <Video
                width={'auto'}
                height={'100%'}
                preload='auto'
                muted
                // poster=''
                playsInline
                autoPlay
                loop
                src={require(`../../animations/${age}/${themeContext.colorMode}/${selectedSymptoms[symptomId].fileName}.mp4`)}
              />
            )}
          </VideoContent>
        </VideoContainer>

        <MenuContainer>
          <MemoizedMenu
            age={age}
            lang={lang}
            elements={selectedSymptoms}
            handleClick={handleMenuClick}
            active={
              symptomId != null && selectedSymptoms[symptomId]
                ? selectedSymptoms[symptomId]
                : null
            }
          />
        </MenuContainer>

        {isMobile && (
          <ToggleButtonContainer>
            <ToggleButton
              onChange={(value) => {
                setAge(value as 'children' | 'adults')
              }}
              choices={[
                {
                  label: lang === 'fr' ? 'Adulte' : 'Erwachsener',
                  value: 'adults',
                },
                {
                  label: lang === 'fr' ? 'Enfant' : 'Kinder',
                  value: 'children',
                },
              ]}
            />
          </ToggleButtonContainer>
        )}
      </Stage>

      {/*
        Texts
      */}
      <Texts>
        <CardContainer>
          <Card
            lang={lang}
            handleToggle={(value) => {
              setAge(value as 'children' | 'adults')
            }}
            symptom={selectedSymptoms[symptomId]}
          />
        </CardContainer>
      </Texts>
    </>
  )
}

const ArrowElement = styled.div`
  border: solid ${({ theme }) => theme.purpleMain};
  border-width: 0 4px 4px 0;
  display: inline-block;
  padding: 12px;
`

const ArrowElementRight = styled(ArrowElement)`
  transform: rotate(-45deg);

  &:hover {
    transform: translateX(5px) rotate(-45deg);
  }
`

const ArrowElementLeft = styled(ArrowElement)`
  transform: rotate(135deg);
`

const Arrow = styled.div`
  cursor: pointer;
  position: absolute;
  top: 50%;
  margin-top: -40px;
  width: 80px;
  height: 80px;
  background: ${({theme}) => theme.colorMode === 'light' ? '#ffffff33': '#00000033'};
  border-radius: 50%;
  color: white;
  align-items: center;
  justify-content: center;
  display: flex;
  line-height: 0;
  z-index: 200;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`

const ArrowPrev = styled(Arrow)`
  left: 15px;
`

const ArrowNext = styled(Arrow)`
  right: 15px;
`

const Label = styled.div`
  font-size: 1em;
  font-family: var(--font-sans-serif);
  opacity: 0.5;
`

const LogosContainer = styled.div`
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 20px;
  width: 100%;
  display: flex;

  img {
    max-height: 25px;
  }

  img:first-child {
    margin-left: 0;
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    img {
      height: 5vw;
      margin: 20px;
    }
  }

  @media ${device.tablet}, @media ${device.laptop} {
    img {
      height: 5vw;
      margin: 12px;
    }
  }
`

const IntroCard = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  line-height: 1.4;

  h4 {
    font-size: 1.5em;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 0.35em;
    margin-top: 0;
  }

  p {
    font-size: 1em;
    // opacity: 0.75;
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 20px;
    box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.025);
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 40px;
    padding-left: 90px;
    box-shadow: none;
  }
`

const IntroCardContainer = styled.div`
  width: 100%;
  max-width: 500px;
  z-index: 100;
  color: #202346;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 0;
    font-size: 1em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 0;
    font-size: 1.25em;
  }
`

const CardContainer = styled.div`
  width: 100%;
  max-width: 500px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 0;
    font-size: 1em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 0;
    font-size: 1.25em;
  }
`
// DVH instead of VH for height (55 and 100)
const Stage = styled(animated.div)`
  position: absolute;
  top: 0;
  width: 100%;
  display: grid;
  z-index: 100;
  height: 100vh;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-template-rows: min-content 1fr min-content;
    height: 55vh;

    &:before {
      content: '';
      position: absolute;
      bottom: -58px;
      left: 0;
      background: linear-gradient(
        0deg,
        ${({ theme }) =>
            theme.colorMode === 'dark'
              ? 'rgba(18, 18, 18, 0)'
              : 'rgba(255, 255, 255, 0)'}
          0%,
        ${({ theme }) =>
            theme.colorMode === 'dark'
              ? 'rgba(18, 18, 18, 1)'
              : 'rgba(255, 255, 255, 1)'}
          69%
      );
      width: 100%;
      height: 60px;
    }
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: 45% 1fr 25%;
    grid-template-rows: 1fr;
    height: 100vh;

    &:before {
      display: none;
    }
  }
`

const Texts = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    height: 45%;
    width: 100%;
    top: auto;
    bottom: 0;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    height: 100%;
    width: 45%;
    top: 0;
    bottom: auto;
  }
`

const StepContent = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 100%;
    z-index: 0;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 35%;
    z-index: 100;
  }
`

const IntroStepContent = styled(StepContent)`
  height: 100vh;
  display: flex;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    margin-top: 5vh;
    align-items: flex-start;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    margin-top: -100vh;
    align-items: center;
  }
`

const MenuContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: min-content 1fr;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    background-color: transparent;
    padding-right: 0px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    background-color: transparent;
    padding-right: 60px;
  }
`

const CanvasVideoContainer = styled.div`
  font-family: var(--font-sans-serif);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0;
  min-width: 0;
  min-height: 0;
  height: 100%;
  background-color: #ffffff;
`

const VideoContainer = styled.div`
  font-family: var(--font-sans-serif);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0;
  min-width: 0;
  min-height: 0;
  height: 100%;
  background-color: ${({ theme }) =>
    theme.colorMode === 'dark' ? '#121212' : '#ffffff'}};
`

const VideoContent = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  justify-content: center;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 5% 0 0 0;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 16% 0;
  }
`

const Video = styled.video`
  // filter: grayscale(40%);
  // opacity: 0.935;
`

const ToggleButtonContainer = styled.div`
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 200;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 0.8em;
    top: 10px;
    left: 20px;
    right: auto;
    display: flex;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 0.85em;
    top: 20px;
    left: auto;
    right: 20px;
    display: none;
  }
`

export default ExampleComponent
